
import { SLOT_ACTIVE_EFFECT_CSS, TEXTURE_ART, coolTimeBoxPolygon, elem, installChrome, setSlotActive, styleInstaller } from "./chrome";
import { skillGradeFor, skillIconNode } from "./itemicon";
import {
  jobForRace,
  skillGroupPageNode,
  skillGroupPreview,
  type SkillGroupPreview,
} from "./skillgroups";
import skillDescTable from "./skilldesc.json";
import { NO_SKILL_STATUS, skillAffectLines, type SkillStatus } from "./skillaffect";
import { isChargeSkill } from "../client/skillreach";

/** `uicharacter.py:282-284` AppendSlotButton — 13x13 `btn_plus_*.sub`. */
const SKILL_PLUS = {
  up: `${TEXTURE_ART}/game/windows/btn_plus_up.png`,
  over: `${TEXTURE_ART}/game/windows/btn_plus_over.png`,
  down: `${TEXTURE_ART}/game/windows/btn_plus_down.png`,
} as const;

/** `Skill_Group_Button_1/_2` -- 43x16 `skill_tab_button_*.sub`
 *  (`characterwindow.py:539-541`). */
const SKILL_TAB = {
  up: `${TEXTURE_ART}/game/windows/skill_tab_button_01.png`,
  over: `${TEXTURE_ART}/game/windows/skill_tab_button_02.png`,
  down: `${TEXTURE_ART}/game/windows/skill_tab_button_03.png`,
} as const;

/** `Skill_Board`, 227x152 behind the active slots
 *  (`characterwindow.py:602`). */
const SKILL_BOARD = `${TEXTURE_ART}/game/windows/skill_board.png`;

/** One slot, exactly as `SkillSlot` delivered it. */
export interface SkillModel {
  readonly slot: number;
  readonly vnum: number;
  readonly level: number;
  readonly maxLevel: number;
  readonly spCost: number;
  readonly cooldownTicks: number;
  /** Ticks remaining when the frame was sent. Counted down locally from there. */
  readonly cooldownLeft: number;
  readonly name: string;
  readonly icon: string;
  readonly flags: number;
}

export interface SkillBookModel {
  /** Skill points left to spend. Derived server-side; never decremented here. */
  readonly points: number;
  /** `POINT_HORSE_SKILL`, what the Riding board's plus spends (`uicharacter.py:942-943`). */
  readonly horsePoints?: number;
  readonly slots: readonly SkillModel[];
}

export const NO_SKILLS: SkillBookModel = { points: 0, slots: [] };

/** Wire bits, mirrored from `net/schema.py`'s `SKILL_*` constants. */
export const SKILL_NEEDS_TARGET = 1 << 0;
export const SKILL_IS_ATTACK = 1 << 1;
export const SKILL_SELF_ONLY = 1 << 2;
/** Whether Combo (122) is currently toggled on. The server's stand-in for the
 *  original's out-of-band `ChatPacket(CHAT_TYPE_COMMAND, "combo %d", ...)`
 *  (`char_skill.cpp:2396`) -- see `net/schema.py`'s own note on the bit. */
export const SKILL_COMBO_ACTIVE = 1 << 3;

export const SKILL_COMBO_VNUM = 122;

export const SUPPORT_SKILL_VNUMS: ReadonlySet<number> = new Set([
  121, 122, 123, 124, 125, 129, 130, 131,
]);

/** The horse skills `RegisterSkill` sets after the class tree
 *  (`playersettingmodule.py:64-81`), shown on the Riding page rather than the
 *  class one (`uicharacter.py:775-777`). */
export const HORSE_SKILL_VNUMS: ReadonlySet<number> = new Set([137, 138, 139, 140]);

/** `Skill_Active_Slot` named cells (`characterwindow.py:605-651`). */
export const SKILL_ACTIVE_CELLS = 24;
/** Where the i-th skill of a tree sits among the 24 `Skill_Active_Slot` cells,
 *  as a position in the grid's column-major fill. Skill slot i+1 is drawn at
 *  slot index i+1 (`uicharacter.py:786-788`): odd indices down the left block
 *  at x=1, even ones down the right block at x=113 (`characterwindow.py:617-650`),
 *  and the two columns beside each are its grade faces, index + 20 per grade
 *  (`uicharacter.py:1208-1209`). */
export function activeCellIndex(i: number): number {
  return (i % 2) * 3 * 4 + Math.floor(i / 2);
}

/** `Skill_ETC_Slot` 6×2 `grid_table` (`characterwindow.py:653-667`). */
export const SKILL_ETC_CELLS = 12;

/** Whether a slot belongs on the original's ETC/support grid rather than its
 *  active one. See `SUPPORT_SKILL_VNUMS`. */
export function isSupportSkill(skill: SkillModel): boolean {
  return SUPPORT_SKILL_VNUMS.has(skill.vnum);
}

export interface SkillBarActions {
  /** Cast. `targetId` is 0 for a skill that takes no target. True when a cast
   *  was sent, as `__UseSkill` answers (`PythonPlayerSkill.cpp:450-630`). */
  onUse?: (vnum: number, targetId: number) => boolean;
  /** `__CheckDashAffect`: a charge is running, so a charge skill's cooldown is
   *  not checked (`PythonPlayerSkill.cpp:274-283`). */
  dashing?: () => boolean;
  /** Put one skill point into a skill. */
  onTrain?: (vnum: number) => void;
  /** What the player currently has selected, or null. Called at click time
   *  rather than pushed in, so the bar never holds a stale selection. */
  target?: () => number | null;
  /** A drag ended on a Fast Access Field (`uitaskbar.py:829-847` AddQuickSlot
   *  with `SLOT_TYPE_SKILL`). Bind, do not cast. */
  onBindQuickSlot?: (quickIndex: number, vnum: number) => void;
  race?: () => number;
  /** The player's status a tooltip's affect sentences evaluate against, read at
   *  hover time as `OverInItem` reads it (`uicharacter.py:620-649`). */
  status?: () => SkillStatus;
}

export function targetFor(skill: SkillModel, selected: number | null): number {
  if (skill.flags & SKILL_SELF_ONLY) return 0;
  return selected ?? 0;
}

/** Whether a click can do anything at all yet. Purely to save a round trip --
 *  the server refuses all three of these itself, and its refusal is the one that
 *  decides. */
export function castable(
  skill: SkillModel,
  selected: number | null,
  remaining: number,
): boolean {
  if (skill.level <= 0) return false;
  if (remaining > 0) return false;
  if (skill.flags & SKILL_NEEDS_TARGET && selected === null) return false;
  return true;
}

/** The keyboard digit a slot answers to. One-based, as the server numbers them. */
export function hotkeyFor(skill: SkillModel): string {
  return String(skill.slot);
}

export function trainable(skill: SkillModel, points: number): boolean {
  if (points <= 0) return false;
  if (skill.level >= skill.maxLevel) return false;
  if (skill.vnum === SKILL_COMBO_VNUM) return false;
  return true;
}

/** How far a cooling slot's fan has swept, in steps of a 32px cell's
 *  perimeter, so the bar repaints when the drawn box moves and not every tick. */
function coolTimeStep(left: number, full: number): number {
  return left > 0 ? Math.floor((1 - left / Math.max(1, full)) * 64) + 1 : 0;
}

/**
 * Locale description then conditions from `skilldesc.txt`
 * (`PythonSkill.cpp:349-362`). Absent when this shard has no row for the vnum.
 */
export function skillDescFor(vnum: number): readonly string[] | undefined {
  const row = (skillDescTable as Record<string, string[]>)[String(vnum)];
  if (row === undefined || row.length === 0) return undefined;
  return row;
}

export function skillHoverText(skill: SkillModel, status: SkillStatus = NO_SKILL_STATUS): string {
  const lines: string[] = [
    skill.level <= 0 ? skill.name : `${skill.name} (level ${skill.level})`,
  ];
  if (skill.level > 0 && skillGradeFor(skill.level) >= 1) {
    lines.push(`${skill.name} Master`);
  }
  const lore = skillDescFor(skill.vnum);
  if (lore !== undefined) {
    for (const line of lore) {
      if (line !== "") lines.push(line);
    }
  }
  lines.push(...skillAffectLines(skill.vnum, skill.level, status));
  return lines.join("\n");
}

const CSS = `
/* Bottom centre, over everything, because it is the one panel that is read
   while fighting rather than between fights. #overlay is a zero-width column
   down the left edge -- fine for the vitals readout, wrong for a horizontal
   strip that has to sit under the character. */
.skillbar {
  position: fixed;
  left: 50%;
  bottom: 12px;
  transform: translateX(-50%);
  z-index: 4;
  display: flex;
  gap: 6px;
  padding: 6px;
  border-radius: 10px;
  background: rgba(12, 14, 18, 0.72);
  border: 1px solid rgba(255, 236, 170, 0.18);
  pointer-events: auto;
  font: 12px/1.3 system-ui, sans-serif;
  color: #d8dee6;
}
.skillbar[hidden] { display: none; }
.skill-drag-ghost {
  position: fixed;
  z-index: 200;
  width: 32px;
  height: 32px;
  margin: -16px 0 0 -16px;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.6));
  opacity: 0.9;
}
.skill-slot-dragging { opacity: 0.45; }
/* CharacterWindow Skill page: Skill_Active_Slot + Skill_ETC_Slot grids
   (characterwindow.py:501-669), not a bottom combat strip. Digit hotkeys
   fire quickslots (char_quickslot.cpp, wired in main.ts), not this bar,
   while the window (and this node) are hidden. */
.charwin-page .skillbar {
  position: absolute;
  inset: 0;
  left: auto;
  bottom: auto;
  transform: none;
  z-index: auto;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  gap: 0;
  padding: 6px 12px 6px;
  border-radius: 0;
  background: transparent;
  border: 0;
  overflow: hidden;
  box-sizing: border-box;
}
/* Active title bar sits at y=17; ETC title at y=200 (characterwindow.py:516,576).
   Section labels stand in for those horizontal bars. Group1/Group2 radio
   buttons (characterwindow.py:528-557) are omitted: the wire sends one tree. */
.charwin-page .skill-section {
  align-items: flex-start;
  gap: 4px;
  width: 100%;
}
.charwin-page .skill-section + .skill-section {
  border-left: 0;
  border-top: 0;
  padding-left: 0;
  margin-left: 0;
  padding-top: 0;
  margin-top: 14px;
}
.charwin-page .skill-section-label {
  font: 11px/1 Tahoma, "Segoe UI", sans-serif;
  letter-spacing: 0;
  text-transform: none;
  color: #ffe3ad;
  padding: 2px 0 4px;
}
.charwin-page .skillbar-points {
  position: absolute;
  top: 10px;
  right: 16px;
  align-self: auto;
  max-width: none;
  padding: 0;
  margin: 0;
  font: 11px/1 Tahoma, "Segoe UI", sans-serif;
  z-index: 1;
}
/* Skill_Board behind the active slots, 227x152 at (13,38)
   (characterwindow.py:602). */
.charwin-page .skill-grid {
  padding: 7px 0 0 3px;
  background: url("${SKILL_BOARD}") no-repeat left top;
  image-rendering: pixelated;
}
/* Skill_Active_Slot cells are 32x32 with col pitch 37 (x_blank 5) and row
   pitch 36 (y_blank 4) — characterwindow.py:617-649. */
.charwin-page .skill-grid {
  grid-template-rows: repeat(4, 32px);
  grid-auto-columns: 32px;
  column-gap: 5px;
  row-gap: 4px;
  justify-content: start;
}
/* Skill_ETC_Slot: 6x2 grid_table, x_step/y_step 32, x_blank 5, y_blank 4
   (characterwindow.py:653-667). */
.charwin-page .skill-grid-support {
  grid-template-columns: repeat(6, 32px);
  grid-auto-rows: 32px;
  column-gap: 5px;
  row-gap: 4px;
  justify-content: start;
}
/* Face from m2-slot (slot_base.png); hit size matches the script's 32x32.
   Re-state the slot background: .skill's own background shorthand would
   otherwise clear the chrome face after the bar is reparented into the page. */
.charwin-page .skill.m2-slot {
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 0;
  border: 0;
  overflow: visible;
  display: block;
  gap: 0;
  box-sizing: border-box;
  background: url("${TEXTURE_ART}/public/slot_base.png") center / 32px 32px no-repeat;
}
.charwin-page .skill.m2-slot:hover:not(:disabled) {
  filter: brightness(1.12);
  background-color: transparent;
  background-image: url("${TEXTURE_ART}/public/slot_base.png");
}
.charwin-page .skill-active.m2-slot {
  outline: 1px solid rgba(255, 236, 170, 0.7);
  outline-offset: -1px;
  background-color: transparent;
  background-image: url("${TEXTURE_ART}/public/slot_base.png");
}
.charwin-page .skill .skill-icon {
  position: absolute;
  left: 0;
  top: 0;
  width: 32px;
  height: 32px;
}
/* Name/SP/hotkey belong in the tooltip (skillHoverText), not on the raster.
   SetSlotCount still paints a small level (uicharacter.py:811,826). */
.charwin-page .skill-name,
.charwin-page .skill-key,
.charwin-page .skill-cost {
  display: none;
}
.charwin-page .skill-level {
  top: auto;
  left: auto;
  right: 1px;
  bottom: 0;
  font: 700 10px/1 Tahoma, "Segoe UI", sans-serif;
  color: #fff;
  text-shadow: 0 0 2px #000, 1px 1px 0 #000;
  z-index: 1;
  pointer-events: none;
}
.charwin-page .skill-cool {
  border-radius: 0;
}
/* Wrap is 32x32; plus sits just outside the bottom-right so the icon stays
   readable (btn_plus is 13x13 — uicharacter.py:282-284). */
.charwin-page .skill-slot-wrap {
  position: relative;
  width: 32px;
  height: 32px;
  overflow: visible;
}
.charwin-page .skill-train {
  top: auto;
  left: auto;
  right: -6px;
  bottom: -6px;
  width: 13px;
  height: 13px;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: url("${SKILL_PLUS.up}") center / 13px 13px no-repeat;
  color: transparent;
  font-size: 0;
  z-index: 2;
}
.charwin-page .skill-train:hover {
  background-image: url("${SKILL_PLUS.over}");
  background-color: transparent;
}
.charwin-page .skill-train:active {
  background-image: url("${SKILL_PLUS.down}");
}
.skill {
  position: relative;
  width: 62px;
  height: 84px;
  padding: 0;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.06);
  color: inherit;
  font: inherit;
  cursor: pointer;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 15px 2px 14px;
}
.skill:hover:not(:disabled) { background: rgba(255, 236, 170, 0.16); }
.skill:disabled { cursor: default; }
.skill-unlearned { opacity: 0.4; }
/* The only bar state the server reports that is not level/cost/cooldown --
   Combo toggled on (SKILL_COMBO_ACTIVE). A pressed look, since the toggle
   is otherwise silent: no cooldown, no cost, no affect to draw. */
.skill-active { border-color: rgba(255, 236, 170, 0.7); background: rgba(255, 236, 170, 0.12); }
.skill-key {
  position: absolute;
  top: 3px;
  left: 5px;
  font-size: 10px;
  color: #9aa7b4;
  font-variant-numeric: tabular-nums;
}
.skill-level {
  position: absolute;
  top: 3px;
  right: 5px;
  font-size: 10px;
  color: #ffe9a8;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.skill-name {
  font-size: 9px;
  line-height: 1.15;
  text-align: center;
  overflow: hidden;
}
/* 30 of the button's 84 pixels. The button grew by the icon's height rather
   than squeezing the name into what was already there: the name is what a
   player who does not yet recognise the art reads, and an icon that cost them
   the second line of "Aura of the Sword" would be a net loss. */
.skill .skill-icon {
  width: 30px;
  height: 30px;
  flex: 0 0 auto;
}
.skill-cost {
  position: absolute;
  bottom: 3px;
  left: 5px;
  font-size: 9px;
  color: #6fa8dc;
  font-variant-numeric: tabular-nums;
}
/* RenderCoolTimeBox's D3DXCOLOR(0, 0, 0, 0.5) (PythonGraphic.cpp:498). */
.skill-cool {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, .5);
  pointer-events: none;
}
.skill-train {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 14px;
  height: 14px;
  padding: 0;
  border-radius: 3px;
  border: 1px solid rgba(255, 236, 170, 0.5);
  background: rgba(255, 236, 170, 0.2);
  color: #ffe9a8;
  font: 700 11px/1 system-ui, sans-serif;
  cursor: pointer;
}
.skill-train:hover { background: rgba(255, 236, 170, 0.4); }
.skillbar-points {
  align-self: center;
  padding: 0 6px;
  font-size: 10px;
  color: #ffe9a8;
  max-width: 62px;
}
.skillbar-points-none { color: #6f7a86; }
/* The original's Skill_Active_Slot (24 named cells) and Skill_ETC_Slot (a
   6x2 grid_table) are two separate boards on the same Skill page
   (characterwindow.py:605-667, uicharacter.py:226-227), not a tab switch.
   A divider plus a small label is this row's stand-in for "two boards" --
   drawn only once a support-classified skill (see SUPPORT_SKILL_VNUMS) is
   actually on the bar, so a bar with nothing to distinguish stays the single
   row it always was. */
.skill-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.skill-section + .skill-section {
  border-left: 1px solid rgba(255, 255, 255, 0.16);
  padding-left: 6px;
  margin-left: 2px;
}
.skill-section-label {
  font-size: 9px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #8a97a6;
}
/* Skill_Active_Slot names 24 fixed cells as two 3-column x 4-row blocks
   (characterwindow.py:614-651); the window's cells are appended in
   column-major order and activeCellIndex puts each skill in its own. */
.skill-grid {
  display: grid;
  grid-template-rows: repeat(4, auto);
  grid-auto-flow: column;
  gap: 6px;
}
/* Skill_ETC_Slot is a 6-column x 2-row grid_table starting at index 101
   (characterwindow.py:653-667) -- a grid_table fills row-major, left to
   right then down, which grid-auto-flow: row on six columns reproduces. */
.skill-grid-support {
  display: grid;
  grid-template-columns: repeat(6, auto);
  grid-auto-flow: row;
  gap: 6px;
}
/* The group-0 preview. SetSlotCount(realSlotIndex, 0) +
   DisableCoverButton(realSlotIndex) (uicharacter.py:822-823): the picture is
   there, the slot is not live. Greyscale rather than a tint so the icon stays
   recognisably itself, which is the whole point of drawing it at all. */
.skill-greyed {
  filter: grayscale(1);
  cursor: default;
  pointer-events: none;
}
/* Skill_Group_Button_1/_2 (characterwindow.py:528-557), the pair
   __SetSkillGroupName shows while the group is zero: one 43x16 radio sprite
   in three states, with the group's own name printed on it. */
.skill-group-buttons {
  display: flex;
  gap: 2px;
  width: 100%;
}
.skill-group-button {
  flex: none;
  width: 43px;
  height: 16px;
  padding: 0;
  border: 0;
  border-radius: 0;
  font: 9px/16px Tahoma, "Segoe UI", sans-serif;
  color: #ffe3ad;
  text-align: center;
  overflow: hidden;
  cursor: pointer;
  background: url("${SKILL_TAB.up}") no-repeat left top;
  image-rendering: pixelated;
}
.skill-group-button:hover { background-image: url("${SKILL_TAB.over}"); }
.skill-group-button[aria-pressed="true"],
.skill-group-button:active { background-image: url("${SKILL_TAB.down}"); }
@media (max-width: 620px) {
  .skill { width: 50px; height: 74px; }
  .skill .skill-icon { width: 26px; height: 26px; }
}
`;

const STYLE_ID = "skillbar-style";

const installStyle = styleInstaller(STYLE_ID, CSS + SLOT_ACTIVE_EFFECT_CSS);

/** Same slop the bag uses: a tap is not a drag. Restated rather than shared
 *  so a joystick-tuning change cannot make skills harder to pick up. */
const DRAG_SLOP_PX = 10;

interface SkillDragState {
  readonly vnum: number;
  readonly pointerId: number;
  readonly startX: number;
  readonly startY: number;
  readonly ghost: HTMLElement;
  readonly button: HTMLButtonElement;
  moved: boolean;
}

export class SkillBar {
  private readonly root: HTMLElement;
  private readonly pointsEl: HTMLElement;
  private model: SkillBookModel = NO_SKILLS;
  private fingerprint = "";
  private dragging: SkillDragState | null = null;
  /** The `click` a real drag's own `pointerup` still produces is not a cast. */
  private justDragged = false;

  /** Which of the two class trees the group-0 preview is showing.
   *  `__SelectSkillGroup(0)` is what `RefreshCharacter` calls at group 0
   *  (`uicharacter.py:1109-1110`), and that presses button 1 and registers
   *  group `0 + 1`. */
  private previewGroup: 1 | 2 = 1;

  /** Ticks left per slot, counted down locally between frames. Keyed by vnum
   *  rather than by position, so a bar that reorders does not carry a cooldown
   *  across to a different skill. */
  private readonly remaining = new Map<number, number>();
  private activeSkills: ReadonlySet<number> = new Set();

  constructor(
    container: HTMLElement,
    private readonly actions: SkillBarActions = {},
  ) {
    const doc = container.ownerDocument ?? document;
    installChrome(doc);
    installStyle(doc);

    this.root = elem(doc, "div", "skillbar");
    this.root.hidden = true;

    this.pointsEl = elem(doc, "div", "skillbar-points");

    container.append(this.root);
  }

  /** CharacterWindow has reparented this node into `.charwin-page`. */
  docked(): void {
    this.paint();
  }

  /** The server stated the whole bar. The only thing that moves this client's
   *  idea of what it knows. */
  update(model: SkillBookModel): void {
    this.model = model;
    // Cooldowns are restated by the server on every frame, so the local
    // countdown is *replaced* rather than reconciled. That is the point: this
    // side never accumulates an error, it only ever interpolates between two
    // authoritative readings.
    this.remaining.clear();
    for (const slot of model.slots) {
      if (slot.cooldownLeft > 0) this.remaining.set(slot.vnum, slot.cooldownLeft);
    }
    this.fingerprint = "";
    this.paint();
  }

  /** One simulation tick has passed. Advances the local countdown only. */
  tick(ticks = 1): void {
    for (const [vnum, left] of this.remaining) {
      const next = left - ticks;
      if (next <= 0) this.remaining.delete(vnum);
      else this.remaining.set(vnum, next);
    }
    this.repaint();
  }

  /** The toggle skills whose slots are lit: `OnActivateSkill` /
   *  `OnDeactivateSkill` (`uicharacter.py:1002-1020`). */
  setActiveSkills(vnums: ReadonlySet<number>): void {
    this.activeSkills = vnums;
    this.repaint();
  }

  /** Ticks left on one skill, as this client currently believes. */
  cooldownOf(vnum: number): number {
    return this.remaining.get(vnum) ?? 0;
  }

  /** Cast by vnum. Fast Access Fields store the skill identity, not the bar
   *  position; digit hotkeys fire quickslots instead (`char_quickslot.cpp`,
   *  wired in `main.ts`), so nothing in production casts by bar position. */
  /** Cast a skill by vnum, as a quick slot or a reserved walk does. True when
   *  a cast was sent. */
  useVnum(vnum: number): boolean {
    const skill = this.model.slots.find((s) => s.vnum === vnum);
    if (skill === undefined) return false;
    return this.cast(skill);
  }

  private cast(skill: SkillModel): boolean {
    const selected = this.actions.target?.() ?? null;
    const charging = isChargeSkill(skill.vnum) && this.actions.dashing?.() === true;
    if (!castable(skill, selected, charging ? 0 : this.cooldownOf(skill.vnum))) return false;
    return this.actions.onUse?.(skill.vnum, targetFor(skill, selected)) ?? false;
  }

  private repaint(): void {
    const print = this.fingerprintOf();
    if (print === this.fingerprint) return;
    this.fingerprint = print;
    this.paint();
  }

  /** One string that changes exactly when the drawn bar must. The cooldowns are
   *  in it as *seconds*, not ticks, so the bar repaints twenty times less often
   *  than it is ticked -- the digit is all that is drawn. */
  private fingerprintOf(): string {
    return [
      this.model.points,
      this.model.horsePoints ?? 0,
      this.actions.race?.() ?? -1,
      this.previewGroup,
      [...this.activeSkills].sort((a, b) => a - b).join(","),
      ...this.model.slots.map(
        (s) =>
          `${s.vnum}:${s.level}:${s.spCost}:` +
          `${coolTimeStep(this.cooldownOf(s.vnum), s.cooldownTicks)}`,
      ),
    ].join("|");
  }

  private paint(): void {
    const doc = this.root.ownerDocument ?? document;
    this.root.replaceChildren();
    const inWindow = this.root.closest(".charwin-page") !== null;
    // The HUD strip hides with nothing to cast. The CharacterWindow Skill
    // page always shows Skill_Active_Slot's 24 faces (`characterwindow.py:605-651`)
    // plus Skill_ETC_Slot's 12 (`:653-667`), empty cells included.
    this.root.hidden = !inWindow && this.model.slots.length === 0;
    if (!inWindow && this.model.slots.length === 0) return;

    const activeSlots = this.model.slots.filter((s) => !isSupportSkill(s) && !HORSE_SKILL_VNUMS.has(s.vnum));
    const supportSlots = this.model.slots.filter((s) => isSupportSkill(s));
    const horseSlots = this.model.slots.filter((s) => HORSE_SKILL_VNUMS.has(s.vnum));

    const preview = inWindow && activeSlots.length === 0 ? this.preview() : null;
    if (preview !== null) {
      this.root.append(
        skillGroupPageNode(preview, this.previewGroup, doc, (group) => {
          this.previewGroup = group;
          this.paint();
        }),
      );
      return;
    }

    this.root.append(this.buildSection("Active", activeSlots, doc, inWindow ? 24 : 0));
    // `SKILL_GROUP_HORSE` (locale_game.txt), the page's button label (`uicharacter.py:1139`).
    if (horseSlots.length > 0) this.root.append(this.buildSection("Riding", horseSlots, doc));
    if (inWindow || supportSlots.length > 0) {
      this.root.append(this.buildSection("Support", supportSlots, doc, inWindow ? 12 : 0));
    }

    this.pointsEl.textContent =
      this.model.points > 0 ? `${this.model.points} skill points` : "";
    this.pointsEl.classList.toggle("skillbar-points-none", this.model.points <= 0);
    if (this.model.points > 0) this.root.append(this.pointsEl);
  }

  /**
   * The greyed group-0 page, or null when the race is not yet known or is not
   * one of the eight. See `skillgroups.ts` for the whole rule.
   */
  private preview(): SkillGroupPreview | null {
    const race = this.actions.race?.();
    if (race === undefined) return null;
    const job = jobForRace(race);
    if (job === null) return null;
    return skillGroupPreview(job, this.previewGroup);
  }

  private buildSection(
    label: string,
    slots: readonly SkillModel[],
    doc: Document,
    minCells = 0,
  ): HTMLElement {
    const section = elem(doc, "div", "skill-section");

    const heading = elem(doc, "span", "skill-section-label", label);
    section.append(heading);

    const row = elem(doc, "div", label === "Support" ? "skill-grid-support" : "skill-grid");
    const empty = (): HTMLElement => {
      const cell = elem(doc, "div", "skill m2-slot skill-unlearned");
      cell.setAttribute("aria-hidden", "true");
      return cell;
    };
    if (label === "Active" && minCells === SKILL_ACTIVE_CELLS && slots.length <= 8) {
      const cells = Array.from({ length: minCells }, empty);
      slots.forEach((skill, i) => {
        cells[activeCellIndex(i)] = this.buildSlotNode(skill, doc);
      });
      row.append(...cells);
    } else {
      for (const skill of slots) row.append(this.buildSlotNode(skill, doc));
      for (let i = slots.length; i < minCells; i++) row.append(empty());
    }
    section.append(row);

    return section;
  }

  /** One slot: the button itself, plus its `+` sibling when one applies. */
  private buildSlotNode(skill: SkillModel, doc: Document): HTMLElement {
    const left = this.cooldownOf(skill.vnum);
    // Always carry m2-slot: CharacterWindow CSS restores the 32x32
    // ICON_SLOT_FILE face (characterwindow.py:617-649) after reparenting;
    // the floating HUD strip's .skill rules keep the larger cards.
    const button = doc.createElement("button");
    button.type = "button";
    button.className = "skill m2-slot";
    if (skill.level <= 0) button.classList.add("skill-unlearned");
    if (skill.flags & SKILL_COMBO_ACTIVE) button.classList.add("skill-active");
    button.dataset.vnum = String(skill.vnum);
    button.title = this.tooltip(skill);
    button.addEventListener("pointerenter", () => {
      button.title = this.tooltip(skill);
    });
    button.addEventListener("click", () => {
      if (this.justDragged) {
        this.justDragged = false;
        return;
      }
      this.cast(skill);
    });
    this.bindDrag(button, skill);

    const key = elem(doc, "span", "skill-key", hotkeyFor(skill));

    const level = elem(doc, "span", "skill-level", skill.level > 0 ? String(skill.level) : "");

    // Null when this build has no picture for the icon name, in which case the
    // button is exactly the name-and-numbers button it was before.
    const art = skillIconNode(skill.icon, skillGradeFor(skill.level), doc);

    const name = elem(doc, "span", "skill-name", skill.name);

    const cost = elem(doc, "span", "skill-cost", skill.level > 0 ? `${skill.spCost} sp` : "-");

    if (art !== null) button.append(art);
    button.append(key, level, name, cost);

    // `SetSlotCoolTime` (`uicharacter.py:999`): the box alone, no number
    // (`PythonSlotWindow.cpp:1051-1055`).
    const polygon = left > 0 ? coolTimeBoxPolygon(1 - left / Math.max(1, skill.cooldownTicks)) : null;
    if (polygon !== null) {
      const cool = elem(doc, "span", "skill-cool");
      cool.style.setProperty("clip-path", polygon);
      button.append(cool);
    }
    // Drawn last, over the icon and the fan (`PythonSlotWindow.cpp:1090-1096`).
    setSlotActive(button, this.activeSkills.has(skill.vnum));

    const pool = HORSE_SKILL_VNUMS.has(skill.vnum) ? (this.model.horsePoints ?? 0) : this.model.points;
    if (trainable(skill, pool)) {
      const train = doc.createElement("button");
      train.type = "button";
      train.className = "skill-train";
      train.textContent = "+";
      train.title = `Put one skill point into ${skill.name}`;
      train.addEventListener("click", (event) => {
        event.stopPropagation();
        this.actions.onTrain?.(skill.vnum);
      });
      const wrap = elem(doc, "div", "skill-slot-wrap");
      wrap.style.position = "relative";
      wrap.append(button, train);
      return wrap;
    }

    return button;
  }

  private bindDrag(button: HTMLButtonElement, skill: SkillModel): void {
    if (this.actions.onBindQuickSlot === undefined) return;
    if (skill.level <= 0) return;
    button.addEventListener("pointerdown", (event) => this.onPointerDown(event, button, skill));
    button.addEventListener("pointermove", (event) => this.onPointerMove(event));
    button.addEventListener("pointerup", (event) => this.onPointerUp(event));
    button.addEventListener("pointercancel", (event) => this.onPointerUp(event));
  }

  private onPointerDown(event: PointerEvent, button: HTMLButtonElement, skill: SkillModel): void {
    if (event.button !== 0) return;
    if (this.dragging !== null) return;
    event.preventDefault();
    button.setPointerCapture(event.pointerId);
    const ghost = elem(button.ownerDocument, "div", "skill-drag-ghost");
    const art = skillIconNode(skill.icon, skillGradeFor(skill.level), button.ownerDocument);
    ghost.append(art ?? button.ownerDocument.createTextNode(skill.name));
    button.ownerDocument.body.append(ghost);
    this.dragging = {
      vnum: skill.vnum,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      ghost,
      button,
      moved: false,
    };
  }

  private onPointerMove(event: PointerEvent): void {
    const drag = this.dragging;
    if (drag === null || event.pointerId !== drag.pointerId) return;
    event.preventDefault();
    if (!drag.moved) {
      if (Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) <= DRAG_SLOP_PX) {
        return;
      }
      drag.moved = true;
      drag.button.classList.add("skill-slot-dragging");
    }
    drag.ghost.style.left = `${event.clientX}px`;
    drag.ghost.style.top = `${event.clientY}px`;
  }

  private onPointerUp(event: PointerEvent): void {
    const drag = this.dragging;
    if (drag === null || event.pointerId !== drag.pointerId) return;
    this.dragging = null;
    drag.button.classList.remove("skill-slot-dragging");
    drag.ghost.remove();
    if (!drag.moved) return;
    this.justDragged = true;
    if (event.type === "pointercancel") return;
    const under = drag.button.ownerDocument.elementFromPoint(event.clientX, event.clientY);
    const destQuick =
      under instanceof Element ? under.closest<HTMLElement>("[data-quick-index]") : null;
    if (destQuick === null) return;
    const quickIndex = Number(destQuick.dataset.quickIndex);
    if (!Number.isInteger(quickIndex) || quickIndex < 0) return;
    this.actions.onBindQuickSlot?.(quickIndex, drag.vnum);
  }

  private tooltip(skill: SkillModel): string {
    return skillHoverText(skill, this.actions.status?.() ?? NO_SKILL_STATUS);
  }

  get element(): HTMLElement {
    return this.root;
  }

  get showing(): boolean {
    return !this.root.hidden;
  }
}
